import { useEffect, useState } from 'react'
import _ from 'lodash'
import { Input, Grid } from '@design-systems/canvas-ui'

import {
  SectionWrapper,
  renderControlLabel,
  renderControlLabelSmallHeaderOnly,
  getUsNumberFormat,
  showErrorMessage,
} from '../Builders'
import { getControlError, isControlError } from '../../common/helper'
import { getRfiTagValues } from '../../common/common'
import { isValidWholeNumber } from 'mvscommoncomponents'

const SECTION_UID = 'footwear_employee_information'

const GROUP_GENDER = 'total_employees_by_gender'
const GROUP_DEMOGRAPHICS = 'total_employees_by_demographics'

const EMPTY_EMPLOYEE_INFORMATION = {
  total_employees: '',
  total_employees_by_gender: {
    number_of_male: '',
    number_of_female: '',
  },
  total_employees_by_demographics: {
    local_employees: '',
    foreign_employees: '',
  },
}

const FootwearEmployeeInformation = (props) => {
  const [sectionData, setSectionData] = useState(
    props.sectionData ?? EMPTY_EMPLOYEE_INFORMATION,
  )

  const [isTriggerSave, setIsTriggerSave] = useState(false)

  useEffect(() => {
    setSectionData(props.sectionData ?? EMPTY_EMPLOYEE_INFORMATION)
  }, [props.sectionData])

  useEffect(() => {
    if (
      isTriggerSave &&
      sectionData &&
      !_.isEqual(props.sectionData, sectionData)
    ) {
      props.saveRfiPayload(sectionData, SECTION_UID)
      setIsTriggerSave(false)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateRootField = (field, value) => {
    setSectionData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const updateNestedField = (group, field, value) => {
    setSectionData((prev) => ({
      ...prev,
      [group]: {
        ...prev?.[group],
        [field]: value,
      },
    }))
  }

  const handleRootNumberChange = (field, event) => {
    const value = event.target.value

    if (value === '') {
      updateRootField(field, '')
      return
    }

    if (isValidWholeNumber(value)) {
      updateRootField(field, Number(value))
    }
  }

  const handleNestedNumberChange = (group, field, event) => {
    const value = event.target.value

    if (value === '') {
      updateNestedField(group, field, '')
      return
    }

    if (isValidWholeNumber(value)) {
      updateNestedField(group, field, Number(value))
    }
  }

  const renderNumberField = ({
    label,
    value,
    error,
    testId,
    mandatory = false,
    onChange,
  }) => {
    return (
      <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
        <div style={{ width: '100%' }}>
          {props.isReadonly
            ? renderControlLabelSmallHeaderOnly({
                label_name: label,
              })
            : renderControlLabel({
                label_name: label,
                is_mandatory: mandatory,
              })}

          {props.isReadonly ? (
            <Input.Label className="hc8-fs-100 hc8-mt-2x">
              {value !== '' && value !== null && value !== undefined
                ? getUsNumberFormat(value)
                : ''}
            </Input.Label>
          ) : (
            <Input.Text
              className="hc8-mt-2x"
              data-testid={testId}
              value={value ?? ''}
              isErrored={error}
              onChange={onChange}
            />
          )}
        </div>
      </Grid.Item>
    )
  }

  const renderSectionBody = () => {
    const totalEmployeesError = getControlError(
      `${props.sectionPath}.total_employees`,
      props.errorCache,
    )

    // Backend emits the mismatch error message on ONE sibling of each pair
    // and an empty string on the other. Use isControlError (presence based)
    // so BOTH inputs show the red border, and pick the non-empty message to
    // render once below the pair.
    const malePath = `${props.sectionPath}.total_employees_by_gender.number_of_male`
    const femalePath = `${props.sectionPath}.total_employees_by_gender.number_of_female`
    const localPath = `${props.sectionPath}.total_employees_by_demographics.local_employees`
    const foreignPath = `${props.sectionPath}.total_employees_by_demographics.foreign_employees`

    const maleErrorMsg = getControlError(malePath, props.errorCache)
    const femaleErrorMsg = getControlError(femalePath, props.errorCache)
    const localErrorMsg = getControlError(localPath, props.errorCache)
    const foreignErrorMsg = getControlError(foreignPath, props.errorCache)

    const hasGenderError =
      isControlError(malePath, props.errorCache) ||
      isControlError(femalePath, props.errorCache)
    const hasDemographicsError =
      isControlError(localPath, props.errorCache) ||
      isControlError(foreignPath, props.errorCache)

    const genderErrorMessage =
      maleErrorMsg ||
      femaleErrorMsg ||
      getRfiTagValues('totalEmployeeInformationGenderMismatchMessage')
    const demographicsErrorMessage =
      localErrorMsg ||
      foreignErrorMsg ||
      getRfiTagValues('totalEmployeeInformationDemographicsMismatchMessage')

    // Backend does not emit an error entry for total_employees itself, but the
    // mismatch is really about that value. Highlight it in red whenever either
    // pair mismatches so the user knows which field to correct.
    const totalEmployeesHasError = Boolean(
      totalEmployeesError || hasGenderError || hasDemographicsError,
    )

    return (
      <div>
        <Input.Label className="hc8-fs-150 font-weight-500 hc8-mh-2x">
          Total Employees
        </Input.Label>

        <Grid.Container className="hc8-mb-3x hc8-mt-4x hc8-ph-2x">
          {renderNumberField({
            label: 'Total Number of Employees/Workers in this factory',
            value: sectionData?.total_employees,
            error: totalEmployeesHasError,
            testId: 'tb-footwear-total-employees',
            mandatory: true,
            onChange: (event) =>
              handleRootNumberChange('total_employees', event),
          })}
        </Grid.Container>

        <Input.Label className="hc8-fs-150 font-weight-500 hc8-mh-2x">
          Number of Employees/Workers by Gender
        </Input.Label>

        <Grid.Container className="hc8-mb-3x hc8-mt-4x hc8-ph-2x">
          {renderNumberField({
            label: 'Male',
            value: sectionData?.total_employees_by_gender?.number_of_male,
            error: hasGenderError,
            testId: 'tb-footwear-male-employees',
            onChange: (event) =>
              handleNestedNumberChange(GROUP_GENDER, 'number_of_male', event),
          })}

          {renderNumberField({
            label: 'Female',
            value: sectionData?.total_employees_by_gender?.number_of_female,
            error: hasGenderError,
            testId: 'tb-footwear-female-employees',
            onChange: (event) =>
              handleNestedNumberChange(GROUP_GENDER, 'number_of_female', event),
          })}

          {!props.isReadonly && hasGenderError && (
            <Grid.Item size={{ xs: 12 }} className="hc8-pt-none">
              {showErrorMessage(genderErrorMessage)}
            </Grid.Item>
          )}
        </Grid.Container>

        <Input.Label className="hc8-fs-150 font-weight-500 hc8-mh-2x">
          Number of Employees by Demographics
        </Input.Label>

        <Grid.Container className="hc8-mb-3x hc8-mt-4x hc8-ph-2x">
          {renderNumberField({
            label: 'Local Employees',
            value:
              sectionData?.total_employees_by_demographics?.local_employees,
            error: hasDemographicsError,
            testId: 'tb-footwear-local-employees',
            onChange: (event) =>
              handleNestedNumberChange(
                GROUP_DEMOGRAPHICS,
                'local_employees',
                event,
              ),
          })}

          {renderNumberField({
            label: 'Foreign Employees',
            value:
              sectionData?.total_employees_by_demographics?.foreign_employees,
            error: hasDemographicsError,
            testId: 'tb-footwear-foreign-employees',
            onChange: (event) =>
              handleNestedNumberChange(
                GROUP_DEMOGRAPHICS,
                'foreign_employees',
                event,
              ),
          })}

          {!props.isReadonly && hasDemographicsError && (
            <Grid.Item size={{ xs: 12 }} className="hc8-pt-none">
              {showErrorMessage(demographicsErrorMessage)}
            </Grid.Item>
          )}
        </Grid.Container>
      </div>
    )
  }

  const footwearSectionTemplate = props.sectionTemplate
    ? {
        ...props.sectionTemplate,
        section_name: props.sectionTemplate.section_name
          ? `${props.sectionTemplate.section_name} (Footwear)`
          : props.sectionTemplate.section_name,
      }
    : props.sectionTemplate

  return (
    <SectionWrapper
      sectionTemplate={footwearSectionTemplate}
      setIsTriggerSave={!props.isReadonly ? setIsTriggerSave : () => {}}
      sectionBody={renderSectionBody()}
      sectionPath={props.sectionPath}
      errorCache={props.errorCache}
    />
  )
}

export default FootwearEmployeeInformation
